import {
  COMMENT,
  CREATED_AT,
  INTEGER,
  REAL,
  TEXT,
  ACTIVE_READING_PLAN,
  AUTH_PROMPT_DISMISSED,
  LAST_LISTENING,
  LAST_READING_CHAPTER,
  BOOKMARK_CHAPTER,
  STORAGE_LANGUAGE,
  THEME_SETTINGS,
  VERSE_OF_DAY_ENABLED,
  VERSE_OF_DAY_HOUR,
  VERSE_OF_DAY_MINUTE,
  PENDING_VERSE_OF_DAY_NAV,
  book_title,
  chapter,
  end_time,
  id,
  similarity,
  start_time,
  timestamp,
  userId,
  verseRange,
  verse_number,
} from '../constants/stringsAndFields';
import {migrateLegacyStorageKeys} from '../config/appStorage';
import {
  checkColumnExist,
  checkTableExists,
  insertData,
  selectData,
} from './common';
import {FAVORITE_QUOTE_TABLE, checkFavoriteQuoteTable} from './favoriteQuote';
import {NOTE_TABLE, checkNoteTable} from './note';
import {READING_PLAN_PROGRESS_TABLE, checkReadingPlanProgressTable} from './readingPlanProgress';
import {runSqlCmd} from './sqlOperations';
import {createAppUserTable, ensureDefaultUserRecord} from './user';
import {LOCAL_DEFAULT_USER_ID} from './userConstants';
import {createUserSettingsTable, ensureDefaultUserSettings} from './userSettings';
import {checkVerseTable, VERSE_TABLE} from './verse';

export const VERSIONS_TABLE = 'versions';

const keys = [id, COMMENT, CREATED_AT];

export const initVersionsTable = async tx => {
  const exist = await checkVersionsTable(tx);
  if (!exist) {
    await reCreateVersionsTable(tx);
  }
};

export const checkOtherMigrations = async tx => {
  try {
    const migrationApplied = [];
    const migrationThreeApplied = await isMigrationApplied(3, tx);
    if (!migrationThreeApplied) {
      await migration3(tx);
      await insertVersion(tx, {
        id: 3,
        [COMMENT]: 'FAVORITE_QUOTE_TABLE table timestamp field',
        [CREATED_AT]: null,
      });
      console.log('Migration 3 is applied');
    }
    const migrationFourApplied = await isMigrationApplied(4, tx);
    if (!migrationFourApplied) {
      await migration4(tx);
      await insertVersion(tx, {
        id: 4,
        [COMMENT]: 'VERSE_TABLE table start, end, similarity field',
        [CREATED_AT]: null,
      });
      migrationApplied.push(4);
      console.log('Migration 4 is applied');
    }
    // migration 5 is table data load
    const migrationSixApplied = await isMigrationApplied(6, tx);
    if (!migrationSixApplied) {
      await migration6(tx);
      await insertVersion(tx, {
        id: 6,
        [COMMENT]: 'FAVORITE_QUOTE and NOTES verseRange field',
        [CREATED_AT]: null,
      });
      console.log('Migration 6 is applied');
    }
    const migrationTenApplied = await isMigrationApplied(10, tx);
    if (!migrationTenApplied) {
      await migration10(tx);
      await insertVersion(tx, {
        id: 10,
        [COMMENT]:
          'AppUser, UserSettings, userId on Note, FavoriteQuote, ReadingPlanProgress',
        [CREATED_AT]: null,
      });
      console.log('Migration 10 is applied');
    }
    const migrationElevenApplied = await isMigrationApplied(11, tx);
    if (!migrationElevenApplied) {
      await migration11();
      await insertVersion(tx, {
        id: 11,
        [COMMENT]: 'Move AsyncStorage keys to MMKV',
        [CREATED_AT]: null,
      });
      console.log('Migration 11 is applied');
    }
    return migrationApplied;
  } catch (e) {
    console.log(e);
  }
};

export const reCreateVersionsTable = async tx => {
  await runSqlCmd(tx, `DROP TABLE IF EXISTS ${VERSIONS_TABLE};`);
  await runSqlCmd(
    tx,
    `CREATE TABLE IF NOT EXISTS ${VERSIONS_TABLE}(
      ${id} INTEGER PRIMARY KEY NOT NULL,
      ${COMMENT} TEXT,
      ${CREATED_AT} TEXT);`,
  );
  const initVersion = {id: 1, [COMMENT]: 'init', [CREATED_AT]: null};
  await insertVersion(tx, initVersion);
};

export const insertVersion = async (tx, version) => {
  await insertData(tx, VERSIONS_TABLE, keys, version);
};

export const checkVersionsTable = async tx => {
  return await checkTableExists(tx, VERSIONS_TABLE);
};

export const isMigrationApplied = async (versionId, tx) => {
  const version = await getVersion(versionId, tx);
  return version && version.length > 0 ? true : false;
};
export const getVersion = async (versionId, tx) => {
  const versions = await selectData(
    tx,
    VERSIONS_TABLE,
    keys,
    id,
    versionId,
    id,
    null,
    'LIMIT 1',
  );
  return versions;
};

const migration3 = async tx => {
  const tableExist = await checkFavoriteQuoteTable(tx);
  if (tableExist) {
    let updatedColumnExists = await checkColumnExist(
      tx,
      FAVORITE_QUOTE_TABLE,
      timestamp,
    );
    if (!updatedColumnExists) {
      await runSqlCmd(
        tx,
        `ALTER TABLE ${FAVORITE_QUOTE_TABLE} ADD COLUMN ${timestamp} ${INTEGER};`,
      );
      updatedColumnExists = await checkColumnExist(
        tx,
        FAVORITE_QUOTE_TABLE,
        timestamp,
      );
      updatedColumnExists && console.log('Migration 2.1 applied successfully');
    }
  }
};

const migration4 = async tx => {
  const tableExist = await checkVerseTable(tx);
  if (tableExist) {
    let updatedColumnExists = await checkColumnExist(
      tx,
      VERSE_TABLE,
      start_time,
    );
    if (!updatedColumnExists) {
      await runSqlCmd(
        tx,
        `ALTER TABLE ${VERSE_TABLE} ADD COLUMN ${start_time} ${REAL};`,
      );
      updatedColumnExists = await checkColumnExist(tx, VERSE_TABLE, start_time);
      updatedColumnExists && console.log('Migration 4.1 applied successfully');
    }

    updatedColumnExists = await checkColumnExist(tx, VERSE_TABLE, end_time);
    if (!updatedColumnExists) {
      await runSqlCmd(
        tx,
        `ALTER TABLE ${VERSE_TABLE} ADD COLUMN ${end_time} ${REAL};`,
      );
      updatedColumnExists = await checkColumnExist(tx, VERSE_TABLE, end_time);
      updatedColumnExists && console.log('Migration 4.2 applied successfully');
    }

    updatedColumnExists = await checkColumnExist(tx, VERSE_TABLE, similarity);
    if (!updatedColumnExists) {
      await runSqlCmd(
        tx,
        `ALTER TABLE ${VERSE_TABLE} ADD COLUMN ${similarity} ${REAL};`,
      );
      updatedColumnExists = await checkColumnExist(tx, VERSE_TABLE, similarity);
      updatedColumnExists && console.log('Migration 4.3 applied successfully');
    }
    await runSqlCmd(
      tx,
      `CREATE INDEX ${chapter}_${VERSE_TABLE}_idx ON ${VERSE_TABLE} (${chapter});
       CREATE INDEX ${book_title}_${VERSE_TABLE}_idx ON ${VERSE_TABLE} (${book_title});
       CREATE INDEX ${verse_number}_${VERSE_TABLE}_idx ON ${VERSE_TABLE} (${verse_number});
      `,
    );
    console.log('Migration 4.4 applied successfully');
  }
};

const migration6 = async tx => {
  const tableExist = await checkFavoriteQuoteTable(tx);
  if (tableExist) {
    let updatedColumnExists = await checkColumnExist(
      tx,
      FAVORITE_QUOTE_TABLE,
      verseRange,
    );
    if (!updatedColumnExists) {
      await runSqlCmd(
        tx,
        `ALTER TABLE ${FAVORITE_QUOTE_TABLE} ADD COLUMN ${verseRange} ${TEXT};`,
      );
      updatedColumnExists = await checkColumnExist(
        tx,
        FAVORITE_QUOTE_TABLE,
        verseRange,
      );
      updatedColumnExists && console.log('Migration 6.1 applied successfully');
    }
  }
  const tableExist2 = await checkNoteTable(tx);
  if (tableExist2) {
    let updatedColumnExists = await checkColumnExist(
      tx,
      NOTE_TABLE,
      verseRange,
    );
    if (!updatedColumnExists) {
      await runSqlCmd(
        tx,
        `ALTER TABLE ${NOTE_TABLE} ADD COLUMN ${verseRange} ${TEXT};`,
      );
      updatedColumnExists = await checkColumnExist(tx, NOTE_TABLE, verseRange);
      updatedColumnExists && console.log('Migration 6.2 applied successfully');
    }
  }
};

const migration10 = async tx => {
  await createAppUserTable(tx);
  await ensureDefaultUserRecord(tx);
  await createUserSettingsTable(tx);
  await ensureDefaultUserSettings(tx);

  const uidLiteral = `'${LOCAL_DEFAULT_USER_ID}'`;
  const userScopedTables = [
    {table: NOTE_TABLE, check: checkNoteTable},
    {table: FAVORITE_QUOTE_TABLE, check: checkFavoriteQuoteTable},
    {table: READING_PLAN_PROGRESS_TABLE, check: checkReadingPlanProgressTable},
  ];

  for (const {table, check} of userScopedTables) {
    const tableExist = await check(tx);
    if (!tableExist) {
      continue;
    }
    let hasUserId = await checkColumnExist(tx, table, userId);
    if (!hasUserId) {
      await runSqlCmd(
        tx,
        `ALTER TABLE ${table} ADD COLUMN ${userId} TEXT DEFAULT ${uidLiteral};`,
      );
      await runSqlCmd(
        tx,
        `UPDATE ${table} SET ${userId} = ${uidLiteral} WHERE ${userId} IS NULL OR ${userId} = '';`,
      );
      hasUserId = await checkColumnExist(tx, table, userId);
      hasUserId && console.log(`Migration 10: ${table}.${userId} added`);
    }
    await runSqlCmd(
      tx,
      `CREATE INDEX IF NOT EXISTS idx_${table}_${userId} ON ${table}(${userId});`,
    );
  }
};

const migration11 = async () => {
  const keysToMigrate = [
    STORAGE_LANGUAGE,
    ACTIVE_READING_PLAN,
    THEME_SETTINGS,
    LAST_READING_CHAPTER,
    LAST_LISTENING,
    BOOKMARK_CHAPTER,
    AUTH_PROMPT_DISMISSED,
    VERSE_OF_DAY_ENABLED,
    VERSE_OF_DAY_HOUR,
    VERSE_OF_DAY_MINUTE,
    PENDING_VERSE_OF_DAY_NAV,
  ];
  const result = await migrateLegacyStorageKeys(keysToMigrate);
  console.log(`Migration 11: moved ${result?.migratedCount ?? 0} storage keys`);
};
